import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, CheckCircle2, ClipboardList } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { notificationApi, type AppNotification } from '../../features/lesson-builder/api/lessonBuilderApi'

// Chuông thông báo trong ứng dụng (ebook): báo HS khi được chấm/feedback, báo GV khi có bài nộp.
const NotificationBell: React.FC = () => {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [unread, setUnread] = useState(0)
  const [items, setItems] = useState<AppNotification[]>([])
  const ref = useRef<HTMLDivElement>(null)

  const load = () => notificationApi.list().then((d) => { setUnread(d.unread); setItems(d.items) }).catch(() => { /* noop */ })

  useEffect(() => {
    if (!isAuthenticated) return
    load()
    const t = window.setInterval(load, 60000)   // làm tươi mỗi phút
    return () => window.clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  if (!isAuthenticated) return null

  const openItem = async (n: AppNotification) => {
    setOpen(false)
    if (!n.is_read) { await notificationApi.markRead(n.id); load() }
    if (n.link) navigate(n.link)
  }
  const readAll = async () => { await notificationApi.markRead(); load() }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setOpen((o) => !o); if (!open) load() }}
        className="relative p-2 rounded-xl text-dark-600 hover:text-primary-600 hover:bg-primary-50 transition-colors"
        title="Thông báo"
        aria-label="Thông báo"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[440px] overflow-auto bg-white rounded-2xl shadow-2xl border border-gray-100 z-50">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 sticky top-0 bg-white">
            <span className="font-bold text-gray-800 text-sm">Thông báo</span>
            {unread > 0 && (
              <button onClick={readAll} className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1">
                <Check size={13} /> Đọc tất cả
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-400">Chưa có thông báo.</div>
          ) : (
            items.map((n) => {
              const Icon = n.kind === 'graded' ? CheckCircle2 : n.kind === 'submitted' ? ClipboardList : Bell
              const color = n.kind === 'graded' ? 'text-emerald-500' : n.kind === 'submitted' ? 'text-blue-500' : 'text-gray-400'
              return (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${n.is_read ? '' : 'bg-blue-50/40'}`}
                >
                  <div className="flex items-start gap-2.5">
                    <Icon size={17} className={`mt-0.5 shrink-0 ${color}`} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-gray-800 leading-snug">{n.title}</span>
                      {n.body && <span className="block text-xs text-gray-500 mt-0.5">{n.body}</span>}
                    </span>
                    {!n.is_read && <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                  </div>
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

export default NotificationBell
